import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { adminAPI, UnreviewedSession, HiddenSession, TurnSummary, RefinementNote, LoggedMetadata, TokenUsageResponse } from '../../services/admin';
import TokenUsageCard from '../../components/admin/TokenUsageCard';
import AnalysisPreviewModal from '../../components/admin/AnalysisPreviewModal';

// Severity badge colors
const severityColors: Record<string, string> = {
  low: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

// issueTypeLabels removed - now using t('analysisReview.issueType.*') directly

function SeverityBadge({ severity }: { severity: string }) {
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${severityColors[severity] || severityColors.medium}`}>
      {severity}
    </span>
  );
}

function QualityStars({ rating, onSelect, editable = false }: { rating: number; onSelect?: (rating: number) => void; editable?: boolean }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          onClick={() => editable && onSelect?.(star)}
          className={`${editable ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
          disabled={!editable}
        >
          <svg
            className={`w-5 h-5 ${star <= rating ? 'text-yellow-400' : 'text-slate-300 dark:text-slate-600'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        </button>
      ))}
    </div>
  );
}

function TurnCard({
  turn,
  turnIndex,
  isExpanded,
  onToggle,
  onViewPrompt,
  onAddNote,
  notes,
}: {
  turn: TurnSummary;
  turnIndex: number;
  isExpanded: boolean;
  onToggle: () => void;
  onViewPrompt: () => void;
  onAddNote: () => void;
  notes: RefinementNote[];
}) {
  const { t } = useTranslation();
  const turnNotes = notes.filter((n) => n.turnIndex === turnIndex);

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
      {/* Turn Header */}
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 flex items-center justify-between text-left hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 px-2 py-0.5 rounded text-sm font-medium">
            {t('analysisReviewPage.turn', { index: turnIndex })}
          </span>
          <span className="text-sm text-slate-600 dark:text-slate-400">
            {turn.toolCallsCount} tool calls | {turn.tokensInput + turn.tokensOutput} tokens | {(turn.durationMs / 1000).toFixed(1)}s
          </span>
          {turnNotes.length > 0 && (
            <span className="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 px-2 py-0.5 rounded text-xs">
              {turnNotes.length} note{turnNotes.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <svg
          className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Turn Content */}
      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={onViewPrompt}
              className="px-3 py-1.5 text-sm bg-slate-100 dark:bg-slate-700 rounded hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
            >
              {t('analysisReviewPage.viewPrompt')}
            </button>
            <button
              onClick={onAddNote}
              className="px-3 py-1.5 text-sm bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 rounded hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-colors"
            >
              {t('analysisReviewPage.addNote')}
            </button>
          </div>

          {/* Response Preview */}
          <div>
            <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{t('analysisReviewPage.aiResponse')}</h4>
            <div className="bg-slate-100 dark:bg-slate-900 rounded p-3 text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap max-h-64 overflow-y-auto">
              {turn.responseTextPreview}
              {turn.responseTextLength > 500 && (
                <span className="text-slate-500">{t('analysisReviewPage.moreCharacters', { count: turn.responseTextLength - 500 })}</span>
              )}
            </div>
          </div>

          {/* Tool Calls */}
          {turn.toolCalls && turn.toolCalls.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{t('analysisReviewPage.toolCalls')}</h4>
              <div className="space-y-2">
                {turn.toolCalls.map((tc, i) => (
                  <div
                    key={i}
                    className={`rounded p-3 text-sm ${
                      tc.hasError
                        ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                        : 'bg-slate-50 dark:bg-slate-800'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono font-medium">{tc.name}</span>
                      <span className="text-slate-500 text-xs">{tc.durationMs}ms</span>
                    </div>
                    <div className="font-mono text-xs text-slate-600 dark:text-slate-400 mb-1">
                      {JSON.stringify(tc.arguments, null, 2).substring(0, 200)}
                    </div>
                    {tc.hasError && tc.error && (
                      <div className="text-red-600 dark:text-red-400 text-xs mt-1">{t('analysisReview.error', { message: tc.error })}</div>
                    )}
                    <div className="text-xs text-slate-500 mt-1">{t('analysisReview.result', { preview: tc.resultPreview })}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Turn Notes */}
          {turnNotes.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{t('analysisReview.turnNotes')}</h4>
              <div className="space-y-2">
                {turnNotes.map((note) => (
                  <div key={note.id} className="bg-orange-50 dark:bg-orange-900/20 rounded p-3 text-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{t('analysisReviewPage.issueType.' + note.issueType)}</span>
                      <SeverityBadge severity={note.severity} />
                    </div>
                    <p className="text-slate-700 dark:text-slate-300">{note.description}</p>
                    {note.suggestedFix && (
                      <p className="text-slate-600 dark:text-slate-400 mt-1 italic">{t('analysisReview.suggestedFix', { fix: note.suggestedFix })}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AddNoteModal({
  sessionId,
  turnIndex,
  onClose,
  onSave,
}: {
  sessionId: string;
  turnIndex: number | null;
  onClose: () => void;
  onSave: (note: RefinementNote) => void;
}) {
  const { t } = useTranslation();
  const [issueType, setIssueType] = useState('reasoning');
  const [severity, setSeverity] = useState('medium');
  const [description, setDescription] = useState('');
  const [suggestedFix, setSuggestedFix] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!description.trim()) return;

    setIsSaving(true);
    try {
      const note = await adminAPI.createNote(sessionId, {
        turnIndex: turnIndex ?? undefined,
        issueType,
        severity,
        description,
        suggestedFix: suggestedFix || undefined,
      });
      onSave(note);
      onClose();
    } catch (err) {
      console.error('Failed to save note:', err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            {turnIndex !== null ? t('analysisReview.addNoteForTurn', { index: turnIndex }) : t('analysisReview.addNoteForSession')}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{t('analysisReview.issueTypeLabel')}</label>
              <select
                value={issueType}
                onChange={(e) => setIssueType(e.target.value)}
                className="w-full px-3 py-2 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              >
                <option value="prompt">{t('analysisReview.optionPrompt')}</option>
                <option value="search">{t('analysisReview.optionSearch')}</option>
                <option value="reasoning">{t('analysisReview.optionReasoning')}</option>
                <option value="output">{t('analysisReview.optionOutput')}</option>
                <option value="tool">{t('analysisReview.optionTool')}</option>
                <option value="other">{t('analysisReview.optionOther')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{t('analysisReview.severity')}</label>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                className="w-full px-3 py-2 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              >
                <option value="low">{t('analysisReview.severityLow')}</option>
                <option value="medium">{t('analysisReview.severityMedium')}</option>
                <option value="high">{t('analysisReview.severityHigh')}</option>
                <option value="critical">{t('analysisReview.severityCritical')}</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{t('analysisReview.descriptionRequired')}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              placeholder={t('analysisReview.descriptionPlaceholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{t('analysisReview.suggestedFixLabel')}</label>
            <textarea
              value={suggestedFix}
              onChange={(e) => setSuggestedFix(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              placeholder={t('analysisReview.suggestedFixPlaceholder')}
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={!description.trim() || isSaving}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? t('common.saving') : t('analysisReview.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PromptModal({ sessionId, turnIndex, onClose }: { sessionId: string; turnIndex: number; onClose: () => void }) {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPrompt = async () => {
      try {
        const data = await adminAPI.getSessionPrompt(sessionId, turnIndex);
        setPrompt(data.systemPrompt);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPrompt();
  }, [sessionId, turnIndex]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{t('analysisReview.promptTitle', { index: turnIndex })}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : error ? (
            <div className="text-red-600 dark:text-red-400">{error}</div>
          ) : (
            <pre className="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300 font-mono bg-slate-50 dark:bg-slate-900 rounded p-4">
              {prompt || t('analysisReview.noPrompt')}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

function SessionReview({
  session,
  onClose,
  onReviewSaved,
}: {
  session: UnreviewedSession;
  onClose: () => void;
  onReviewSaved: () => void;
}) {
  const { t } = useTranslation();
  const [turns, setTurns] = useState<TurnSummary[]>([]);
  const [metadata, setMetadata] = useState<LoggedMetadata | null>(null);
  const [notes, setNotes] = useState<RefinementNote[]>([]);
  const [tokenUsage, setTokenUsage] = useState<TokenUsageResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTokens, setIsLoadingTokens] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedTurn, setExpandedTurn] = useState<number | null>(null);
  const [promptModal, setPromptModal] = useState<{ sessionId: string; turnIndex: number } | null>(null);
  const [noteModal, setNoteModal] = useState<{ sessionId: string; turnIndex: number | null } | null>(null);
  const [previewModal, setPreviewModal] = useState<string | null>(null);
  const [qualityRating, setQualityRating] = useState(0);
  const [reviewNotes, setReviewNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [turnsData, notesData] = await Promise.all([
          adminAPI.getSessionTurns(session.id),
          adminAPI.getSessionNotes(session.id),
        ]);
        setTurns(turnsData.turns);
        setMetadata(turnsData.metadata);
        setNotes(notesData.notes);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();

    // Fetch token usage separately (non-blocking)
    const fetchTokenUsage = async () => {
      try {
        const tokenData = await adminAPI.getSessionTokenUsage(session.id);
        setTokenUsage(tokenData);
      } catch (err) {
        console.error('Failed to load token usage:', err);
      } finally {
        setIsLoadingTokens(false);
      }
    };
    fetchTokenUsage();
  }, [session.id]);

  const handleSaveReview = async () => {
    if (qualityRating === 0) return;

    setIsSaving(true);
    try {
      await adminAPI.submitReview(session.id, {
        qualityRating,
        reviewNotes: reviewNotes || undefined,
      });
      onReviewSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleNoteSaved = (note: RefinementNote) => {
    setNotes((prev) => [...prev, note]);
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-slate-800 rounded-xl p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-slate-600 dark:text-slate-400">{t('analysisReview.loadingSession')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl max-w-5xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{t('analysisReview.sessionOverview')}</h3>
            <p className="text-sm text-slate-500">{session.id}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setPreviewModal(session.id)}
              className="px-3 py-1.5 text-sm bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400 rounded hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors flex items-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              {t('analysisReview.analysisPreview')}
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {/* Metadata */}
          {metadata && (
            <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4">
              <h4 className="font-medium text-slate-900 dark:text-white mb-3">{t('analysisReview.sessionSummary')}</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">{t('analysisReview.model')}</span>{' '}
                  <span className="font-medium">{metadata.modelId}</span>
                </div>
                <div>
                  <span className="text-slate-500">{t('analysisReview.turnsLabel')}</span>{' '}
                  <span className="font-medium">{metadata.totalTurns}</span>
                </div>
                <div>
                  <span className="text-slate-500">{t('analysisReview.tokens')}</span>{' '}
                  <span className="font-medium">{metadata.totalTokensInput + metadata.totalTokensOutput}</span>
                </div>
                <div>
                  <span className="text-slate-500">{t('analysisReview.toolCallsLabel')}</span>{' '}
                  <span className="font-medium">{metadata.totalToolCalls}</span>
                </div>
                <div>
                  <span className="text-slate-500">{t('analysisReview.time')}</span>{' '}
                  <span className="font-medium">{metadata.totalDurationMs ? `${(metadata.totalDurationMs / 1000).toFixed(1)}s` : 'N/A'}</span>
                </div>
                <div>
                  <span className="text-slate-500">{t('analysisReview.statusLabel')}</span>{' '}
                  <span className="font-medium">{metadata.status}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-slate-500">{t('analysisReview.tools')}</span>{' '}
                  <span className="font-medium">{metadata.uniqueToolsUsed.join(', ')}</span>
                </div>
              </div>
              {/* Token Usage Card */}
              <TokenUsageCard tokenUsage={tokenUsage} isLoading={isLoadingTokens} />
            </div>
          )}

          {/* Document Preview */}
          <div>
            <h4 className="font-medium text-slate-900 dark:text-white mb-2">{t('common.document')}</h4>
            <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-sm text-slate-700 dark:text-slate-300">
              {session.documentPreview}
            </div>
          </div>

          {/* Session-level note button */}
          <button
            onClick={() => setNoteModal({ sessionId: session.id, turnIndex: null })}
            className="px-3 py-2 text-sm bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 rounded hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-colors"
          >
            {t('analysisReview.addSessionNote')}
          </button>

          {/* Session-level notes */}
          {notes.filter((n) => n.turnIndex === null).length > 0 && (
            <div>
              <h4 className="font-medium text-slate-900 dark:text-white mb-2">{t('analysisReview.sessionNotes')}</h4>
              <div className="space-y-2">
                {notes
                  .filter((n) => n.turnIndex === null)
                  .map((note) => (
                    <div key={note.id} className="bg-orange-50 dark:bg-orange-900/20 rounded p-3 text-sm">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{t('analysisReview.issueType.' + note.issueType)}</span>
                        <SeverityBadge severity={note.severity} />
                      </div>
                      <p className="text-slate-700 dark:text-slate-300">{note.description}</p>
                      {note.suggestedFix && (
                        <p className="text-slate-600 dark:text-slate-400 mt-1 italic">{t('analysisReview.suggestedFix', { fix: note.suggestedFix })}</p>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Turns */}
          <div>
            <h4 className="font-medium text-slate-900 dark:text-white mb-3">{t('analysisReview.turns', { count: turns.length })}</h4>
            <div className="space-y-3">
              {turns.map((turn, index) => (
                <TurnCard
                  key={index}
                  turn={turn}
                  turnIndex={index}
                  isExpanded={expandedTurn === index}
                  onToggle={() => setExpandedTurn(expandedTurn === index ? null : index)}
                  onViewPrompt={() => setPromptModal({ sessionId: session.id, turnIndex: index })}
                  onAddNote={() => setNoteModal({ sessionId: session.id, turnIndex: index })}
                  notes={notes}
                />
              ))}
              {turns.length === 0 && (
                <p className="text-slate-500 dark:text-slate-400 text-center py-8">{t('analysisReview.noTurnData')}</p>
              )}
            </div>
          </div>

          {/* Review Section */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
            <h4 className="font-medium text-slate-900 dark:text-white mb-4">{t('analysisReview.yourRating')}</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-600 dark:text-slate-400 mb-2">{t('analysisReview.qualityRating')}</label>
                <QualityStars rating={qualityRating} onSelect={setQualityRating} editable />
              </div>
              <div>
                <label className="block text-sm text-slate-600 dark:text-slate-400 mb-2">{t('analysisReview.notesOptional')}</label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  placeholder={t('analysisReview.notesPlaceholder')}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSaveReview}
            disabled={qualityRating === 0 || isSaving}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? t('common.saving') : t('analysisReview.saveRating')}
          </button>
        </div>
      </div>

      {/* Modals */}
      {promptModal && <PromptModal sessionId={promptModal.sessionId} turnIndex={promptModal.turnIndex} onClose={() => setPromptModal(null)} />}
      {noteModal && (
        <AddNoteModal
          sessionId={noteModal.sessionId}
          turnIndex={noteModal.turnIndex}
          onClose={() => setNoteModal(null)}
          onSave={handleNoteSaved}
        />
      )}
      {previewModal && (
        <AnalysisPreviewModal
          sessionId={previewModal}
          onClose={() => setPreviewModal(null)}
        />
      )}
    </div>
  );
}

export default function AnalysisReviewPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'pl' ? 'pl-PL' : 'en-US';
  const [unreviewedSessions, setUnreviewedSessions] = useState<UnreviewedSession[]>([]);
  const [hiddenSessions, setHiddenSessions] = useState<HiddenSession[]>([]);
  const [unfixedNotes, setUnfixedNotes] = useState<RefinementNote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSession, setSelectedSession] = useState<UnreviewedSession | null>(null);
  const [activeTab, setActiveTab] = useState<'unreviewed' | 'hidden' | 'unfixed'>('unreviewed');

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [sessionsData, hiddenData, notesData] = await Promise.all([
        adminAPI.getUnreviewedSessions(50),
        adminAPI.getHiddenSessions(50),
        adminAPI.getUnfixedNotes({ limit: 50 }),
      ]);
      setUnreviewedSessions(sessionsData.sessions);
      setHiddenSessions(hiddenData.sessions);
      setUnfixedNotes(notesData.notes);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleReviewSaved = () => {
    fetchData();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{t('analysisReview.title')}</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">{t('analysisReview.subtitle')}</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('unreviewed')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'unreviewed'
              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
              : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
          }`}
        >
          {t('analysisReview.toReview')} ({unreviewedSessions.length})
        </button>
        <button
          onClick={() => setActiveTab('hidden')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'hidden'
              ? 'bg-slate-600 text-white dark:bg-slate-500 dark:text-white'
              : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
          }`}
        >
          {t('analysisReview.hiddenTab')} ({hiddenSessions.length})
        </button>
        <button
          onClick={() => setActiveTab('unfixed')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'unfixed'
              ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
              : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
          }`}
        >
          {t('analysisReview.unfixedNotes')} ({unfixedNotes.length})
        </button>
      </div>

      {/* Unreviewed Sessions Tab */}
      {activeTab === 'unreviewed' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('common.date')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('common.document')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('analysisReview.autoScore')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('analysisReview.logs')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('analysisReview.cost')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {unreviewedSessions.map((session) => (
                  <tr key={session.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/30">
                    <td className="px-4 py-3 text-sm text-slate-900 dark:text-white">
                      {new Date(session.createdAt).toLocaleDateString(locale, {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 max-w-xs truncate">
                      {session.documentPreview || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {session.evaluationScore !== null ? (
                        <span className="font-medium">{session.evaluationScore}</span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {session.hasLogs ? (
                        <span className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 px-2 py-0.5 rounded text-xs">
                          {t('analysisReview.logsAvailable')}
                        </span>
                      ) : (
                        <span className="bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400 px-2 py-0.5 rounded text-xs">
                          {t('analysisReview.logsNone')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {session.costPln > 0 ? (
                        <span className="font-medium text-green-600 dark:text-green-400">
                          {session.costPln.toFixed(2)} zł
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setSelectedSession(session)}
                          disabled={!session.hasLogs}
                          className="px-3 py-1 text-sm bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {t('analysisReview.review')}
                        </button>
                        <button
                          onClick={async () => {
                            await adminAPI.hideSession(session.id);
                            fetchData();
                          }}
                          className="px-3 py-1 text-sm bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400 rounded hover:bg-slate-200 dark:hover:bg-slate-600"
                          title="Ukryj z listy"
                        >
                          {t('analysisReview.hide')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {unreviewedSessions.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                      {t('analysisReview.allReviewed')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Hidden Sessions Tab */}
      {activeTab === 'hidden' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('common.date')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('common.document')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('analysisReview.hiddenAt')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('analysisReview.cost')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {hiddenSessions.map((session) => (
                  <tr key={session.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/30">
                    <td className="px-4 py-3 text-sm text-slate-900 dark:text-white">
                      {new Date(session.createdAt).toLocaleDateString(locale, {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 max-w-xs truncate">
                      {session.documentPreview || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
                      {new Date(session.hiddenAt).toLocaleDateString(locale, {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {session.costPln > 0 ? (
                        <span className="font-medium text-green-600 dark:text-green-400">
                          {session.costPln.toFixed(2)} zł
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={async () => {
                          await adminAPI.unhideSession(session.id);
                          fetchData();
                        }}
                        className="px-3 py-1 text-sm bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 rounded hover:bg-green-200 dark:hover:bg-green-900/50"
                      >
                        {t('analysisReview.unhide')}
                      </button>
                    </td>
                  </tr>
                ))}
                {hiddenSessions.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                      {t('analysisReview.noHiddenSessions')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Unfixed Notes Tab */}
      {activeTab === 'unfixed' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="p-4 space-y-3">
            {unfixedNotes.map((note) => (
              <div key={note.id} className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium">{t('analysisReview.issueType.' + note.issueType)}</span>
                      <SeverityBadge severity={note.severity} />
                      {note.turnIndex !== null && (
                        <span className="text-xs text-slate-500">Turn {note.turnIndex}</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-700 dark:text-slate-300 mb-2">{note.description}</p>
                    {note.suggestedFix && (
                      <p className="text-sm text-slate-600 dark:text-slate-400 italic">
                        {t('analysisReview.suggestedFix', { fix: note.suggestedFix })}
                      </p>
                    )}
                    <p className="text-xs text-slate-500 mt-2">
                      {t('analysisReview.sessionPrefix')} {note.sessionId.substring(0, 8)}... |{' '}
                      {new Date(note.createdAt).toLocaleDateString(locale)}
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      const version = prompt(t('analysisReview.enterVersion'));
                      if (version) {
                        await adminAPI.markNoteFixed(note.id, version);
                        fetchData();
                      }
                    }}
                    className="px-3 py-1 text-sm bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 rounded hover:bg-green-200 dark:hover:bg-green-900/50"
                  >
                    {t('analysisReview.markFixed')}
                  </button>
                </div>
              </div>
            ))}
            {unfixedNotes.length === 0 && (
              <p className="text-center text-slate-500 dark:text-slate-400 py-8">
                {t('analysisReview.noUnfixedNotes')}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Session Review Modal */}
      {selectedSession && (
        <SessionReview
          session={selectedSession}
          onClose={() => setSelectedSession(null)}
          onReviewSaved={handleReviewSaved}
        />
      )}
    </div>
  );
}
